/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import clsx from 'clsx';
import React, { useState, DragEvent, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { useParams } from 'react-router-dom';

import { Logger } from '@futrli/shared-services';
import { FormattedMessage } from '@futrli/localisation';
import { ForecastComparisonResponse, ReportTemplateDetail, RowFormula } from '@futrli-sdk/predict';
import { UILoader } from '@futrli/ui-components';

import { DeleteItemModal } from './components/delete-item-modal/delete-item-modal';
import { Sidebar } from './components/sidebar/sidebar';
import { TopBar } from './components/top-bar/top-bar';
import { PageSetupMenu } from './components/page-setup-menu/page-setup-menu';
import { ZoomMenu } from './components/zoom-menu/zoom-menu';
import { useReportBuilderContext } from './report-builder.context';
import { LayoutItem as Layout, DroppedEvent } from './components/grid-layout';
import {
  APP_WIDGET_NAMES,
  CARD_TYPES,
  DataField,
  FinData,
  LayoutData,
  ReportMappingData,
  MappingData,
  WIDGET_TYPES
} from './types';
import {
  WIDGET_VERSION,
  FETCH_DATA_DELAY,
  LAYOUT_STEP,
  defaultCalendarOptions,
  TOP_BAR_HEIGHT,
  ONBOARDING_TOP_BAR_HEIGHT
} from './constants';
import { flattenFinFields, mapWorkspaceTemplateDataRefs, replaceRefsInWidget, resolveFormulaRefs } from './utils-data';
import { createNewRowFormula } from '../core/api/reporting/report-builder';
import { fetchAvailableRowFormulaLineData } from '../core/api/financial-config';
import { usePredictionsInfoContext } from '../forecast/detail/context/predictions-info-context';
import { DeletePageModal } from './components/delete-page-modal/delete-page-modal';
import { ReportIntlProvider } from './components/report-intl-provider/report-intl-provider';
import { ReportRenderer } from './components/report-renderer/report-renderer';
import { ReportTitle } from './components/report-title/report-title';
import { useTemplatesContext } from './templates.context';
import { MapDatasetsFutrliTemplateModal } from './components/map-template-data-modal/map-datasets-futrli-template-modal';
import { MapTemplateDataModal } from './components/map-template-data-modal/map-template-data-modal';
import { trackEvent } from '../core/tracker';
import { TRACKING_EVENTS } from '../core/constants';
import { OnboardingReportHeader } from './components/onboarding/onboarding-report-header/onboarding-report-header';
import { OnboardingProgressDetection } from './components/onboarding/onboarding-progress-detection/onboarding-progress-detection';
import { BottomControls } from './components/onboarding/bottom-controls/bottom-controls';
import { useOnboardingContext } from './onboarding.context';
import { OnboardingTopBar } from './components/onboarding/onboarding-top-bar/onboarding-top-bar';
import {
  cleanUpLayout,
  getAvailableSpace,
  getCorrectedTargetWidthStep,
  getSourceTemplateIdToInsert,
  getWidgetIdsByPage,
  getWidthStepFromPosition,
  gridVerticalUnitsToPX,
  inchesToPX,
  pageContainsMultiPagesTableFromPrevPages,
  pXToGridVerticalUnits
} from './utils-layout';
import { useI18nConstants } from './use-i18n-constants';

import styles from './report-builder.scss';
import { useNavHeight } from '../hooks/useNavHeight';

export const ReportBuilder = () => {
  const { workspaceId, organisationId } = useParams();
  const { mapTemplateDatasets, mapWorkspaceTemplateDatasets } = useI18nConstants();
  const { isConsolidation } = usePredictionsInfoContext();

  const [sideBarOpen, setSidebarOpen] = useState(true);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [pageToDelete, setPageToDelete] = useState<number | null>(null);

  const [isPageSetupMenuOpen, setIsPageSetupMenuOpen] = useState(false);
  const [isZoomDropdownOpen, setIsZoomDropdownOpen] = useState(false);

  const [printState, setPrintState] = useState(false);
  const [insertingTemplate, setInsertingTemplate] = useState(false);
  const [isAwaitingData, setIsAwaitingData] = useState(false);
  const scrollableContent = useRef<HTMLDivElement>(null);
  const scrollTimer = useRef<number>();

  const [contentScrollPosition, setContentScrollPosition] = useState(0);

  const { navHeight } = useNavHeight();

  const {
    pageLayout,
    pageLayoutData,
    setPageLayout,
    setPageLayoutData,
    setCurrentCardId,
    layoutFinData,
    layoutComparisonData,
    setLayoutFinData,
    setLayoutComparisonData,
    pageSetup: { margins, orientation, backgroundColor },
    headerFooterLimits,
    getPageHeight,
    getPageWidth,
    heightAdjusted,
    pageBreaks,
    setPageBreaks,
    forecasts,
    setWorkAreaWidth,
    isPendingNewFinData,
    setIsPendingNewFinData,
    mapDatasetsModalOpen,
    setMapDatasetsModalOpen,
    mapTemplateDataModalOpen,
    setMapTemplateDataModalOpen,
    setDatasetsToMap,
    setDataRefsToMap,
    reportSourceTemplateId,
    allRequestsFinished,
    allFinFieldsArray,
    rowFormulaData,
    dataReferenceMapping,
    setDataReferenceMapping,
    isOnboarding,
    itemToBringIntoView,
    setItemToBringIntoView,
    scrollReportTo,
    locale,
    getRowFormulas
  } = useReportBuilderContext();
  // A source template is pending insertion only for legacy report shells. Reports created by the
  // new sidebar already contain the template layout and must not enter the insertion/loading flow.
  const [insertTemplateId, setInsertTemplateId] = useState(() =>
    getSourceTemplateIdToInsert(reportSourceTemplateId, pageLayout)
  );
  const { fetchTemplateById } = useTemplatesContext();
  const { handleEnd } = useOnboardingContext();

  const workAreaRef = useRef<HTMLDivElement | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);

  const workAreaHeight = `calc(100vh - ${
    navHeight + TOP_BAR_HEIGHT + (isOnboarding ? ONBOARDING_TOP_BAR_HEIGHT : 0)
  }px`;

  useEffect(() => {
    heightAdjusted.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation]);

  useEffect(() => {
    if (itemToBringIntoView) {
      window.clearInterval(scrollTimer.current);
      scrollTimer.current = window.setTimeout(() => {
        const element = document.getElementById(itemToBringIntoView);
        if (element && scrollableContent.current) {
          const elementPos = (element.children[0] || element).getBoundingClientRect();
          const containerPos = scrollableContent.current.getBoundingClientRect();
          const gutter = 10;

          scrollableContent.current.scrollTo({
            behavior: 'smooth',
            top: elementPos.top - containerPos.top + scrollableContent.current.scrollTop - gutter,
            left: elementPos.left - containerPos.left + scrollableContent.current.scrollLeft - gutter
          });
        }
        setItemToBringIntoView(null);
      }, 200);
    }
  }, [itemToBringIntoView, setItemToBringIntoView]);

  useEffect(() => {
    if (scrollReportTo && scrollableContent.current) {
      scrollableContent.current.scrollTo({
        behavior: 'smooth',
        top: scrollReportTo[1],
        left: scrollReportTo[0]
      });
    }
  }, [scrollReportTo]);

  useEffect(() => {
    const fetchAndInsertTemplate = async (templateId: string) => {
      const template = await fetchTemplateById(templateId);
      if (template) {
        insertTemplate(template, []);
      } else {
        Logger.warn('Could not fetch template details!');
      }
    };

    if (allRequestsFinished && insertTemplateId && pageLayout.length === 0) {
      setInsertTemplateId('');
      fetchAndInsertTemplate(insertTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRequestsFinished, insertTemplateId, pageLayout, forecasts, organisationId, workspaceId, dataReferenceMapping]);

  const showLoader = useMemo(() => {
    return !allRequestsFinished || isPendingNewFinData || !!insertTemplateId || insertingTemplate || isAwaitingData;
  }, [allRequestsFinished, isPendingNewFinData, insertTemplateId, insertingTemplate, isAwaitingData]);

  useLayoutEffect(() => {
    const updateWorkAreaWidth = () => {
      setWorkAreaWidth((workAreaRef.current?.clientWidth || 120) - 120);
    };
    updateWorkAreaWidth();

    window.addEventListener('resize', updateWorkAreaWidth);

    return () => {
      window.removeEventListener('resize', updateWorkAreaWidth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workAreaRef.current?.clientWidth]);

  const onDeleteItem = () => {
    if (itemToDelete !== null) {
      if (pageLayout.length === 1 && pageLayout[0].i === itemToDelete) {
        setPageLayout([]);
        setPageLayoutData([]);
      } else {
        setPageLayout(pageLayout.filter((el: Layout) => el.i !== itemToDelete));
        setPageLayoutData(pageLayoutData.filter((el: LayoutData) => el.i !== itemToDelete));
      }
      setCurrentCardId('');
      setItemToDelete(null);
      heightAdjusted.current = false;
    }
  };

  const onDuplicateItem = (itemToDuplicate: string) => {
    const duplicatedItem = pageLayout.find(widget => widget.i === itemToDuplicate);
    const duplicatedItemData = pageLayoutData.find(widget => widget.i === itemToDuplicate) as LayoutData;
    let newLayout = [...pageLayout];
    if (duplicatedItem) {
      const itemWidth = duplicatedItem?.w;
      const itemXPosition = duplicatedItem?.x;
      const pageWidth = pXToGridVerticalUnits(getPageWidth());
      const newId = uuid();
      // positioning to the left if there's enough space
      if (itemXPosition >= itemWidth) {
        newLayout = [
          ...pageLayout,
          {
            ...duplicatedItem,
            x: itemXPosition - itemWidth,
            i: newId
          }
        ];
        // positioning to the right if there's enough space
      } else if (pageWidth - (itemXPosition + itemWidth) >= itemWidth) {
        newLayout = [
          ...pageLayout,
          {
            ...duplicatedItem,
            x: itemXPosition + itemWidth,
            i: newId
          }
        ];
        // positioning to the bottom
      } else {
        newLayout = [
          ...pageLayout,
          {
            ...duplicatedItem,
            x: itemXPosition,
            y: duplicatedItem?.y + duplicatedItem?.h,
            i: newId
          }
        ];
      }
      setPageLayout(newLayout);
      setPageLayoutData([
        ...pageLayoutData,
        {
          ...duplicatedItemData,
          i: newId
        }
      ]);
      setCurrentCardId(newId);
      setItemToBringIntoView(newId);
      if (layoutFinData[itemToDuplicate]) {
        setLayoutFinData({ ...layoutFinData, [newId]: layoutFinData[itemToDuplicate] });
      }
      if (layoutComparisonData[itemToDuplicate]) {
        setLayoutComparisonData({ ...layoutComparisonData, [newId]: layoutComparisonData[itemToDuplicate] });
      }
      setTimeout(() => updateLayoutState(newLayout), 200);
    }
  };

  const onAddPageClick = () => {
    setPageBreaks(pageBreaks + 1);
  };

  const onAddPageBefore = (
    beforePage: number,
    widgetsToAdd?: Layout[],
    widgetsDataToAdd?: LayoutData[],
    numberOfPages = 1
  ) => {
    setPageBreaks(pageBreaks + numberOfPages);
    const pageHeight = getPageHeight();
    const newPageLayout = pageLayout.map(pl => {
      const npl = { ...pl };
      const yPX = gridVerticalUnitsToPX(npl.y);
      const widgetPage = Math.floor((yPX + inchesToPX(margins[0].value)) / pageHeight);
      if (widgetPage >= beforePage) {
        npl.y = pXToGridVerticalUnits(yPX + pageHeight * numberOfPages);
      }
      return npl;
    });
    if (widgetsToAdd?.length && widgetsDataToAdd?.length) {
      setPageLayout([...newPageLayout, ...widgetsToAdd]);
      setPageLayoutData([...pageLayoutData, ...widgetsDataToAdd]);
    } else {
      setPageLayout(newPageLayout);
    }
  };

  const onDuplicatePage = (pageToDuplicate: number) => {
    const widgetIdsToBeDuplicated = getWidgetIdsByPage(pageToDuplicate, pageLayout, getPageHeight());
    const widgetsToBeDuplicated = pageLayout.filter(pl => widgetIdsToBeDuplicated.indexOf(pl.i) !== -1);
    const widgetsDataToBeDuplicated = pageLayoutData.filter(pl => widgetIdsToBeDuplicated.indexOf(pl.i) !== -1);
    const newFinData: Record<string, Record<string, FinData> | undefined> = {};
    const newComparisonData: Record<string, ForecastComparisonResponse | undefined> = {};

    const widgetsDataToAdd: LayoutData[] = [];
    const widgetsToAdd = widgetsToBeDuplicated.map(pl => {
      const newId = uuid();
      if (layoutFinData[pl.i]) {
        newFinData[newId] = layoutFinData[pl.i];
      }
      if (layoutComparisonData[pl.i]) {
        newComparisonData[newId] = layoutComparisonData[pl.i];
      }
      const widgetData = widgetsDataToBeDuplicated.find(e => e.i === pl.i);
      if (widgetData) {
        widgetsDataToAdd.push({ ...widgetData, i: newId });
      } else {
        Logger.warn('found a layout item without layout data!');
      }

      return {
        ...pl,
        i: newId
      };
    });
    setLayoutFinData({ ...layoutFinData, ...newFinData });
    setLayoutComparisonData({ ...layoutComparisonData, ...newComparisonData });
    onAddPageBefore(pageToDuplicate, widgetsToAdd, widgetsDataToAdd);
  };

  const onMovePageUp = (pageToMove: number) => {
    if (pageToMove > 0) {
      const pageHeight = getPageHeight();
      const newPageLayout = pageLayout.map(pl => {
        const npl = { ...pl };
        const yPX = gridVerticalUnitsToPX(pl.y);
        const widgetPage = Math.floor(yPX / pageHeight);
        if (widgetPage === pageToMove) {
          npl.y = pXToGridVerticalUnits(yPX - pageHeight);
        }
        if (widgetPage === pageToMove - 1) {
          npl.y = pXToGridVerticalUnits(yPX + pageHeight);
        }
        return npl;
      });
      setPageLayout(newPageLayout);
    }
  };

  const onMovePageDown = (pageToMove: number) => {
    if (pageToMove < pageBreaks - 1) {
      const pageHeight = getPageHeight();
      const newPageLayout = pageLayout.map(pl => {
        const npl = { ...pl };
        const yPX = gridVerticalUnitsToPX(pl.y);
        const widgetPage = Math.floor(yPX / pageHeight);
        if (widgetPage === pageToMove) {
          npl.y = pXToGridVerticalUnits(yPX + pageHeight);
        }
        if (widgetPage === pageToMove + 1) {
          npl.y = pXToGridVerticalUnits(yPX - pageHeight);
        }
        return npl;
      });
      setPageLayout(newPageLayout);
    }
  };

  const onDeletePage = () => {
    if (pageToDelete !== null) {
      const pageHeight = getPageHeight();
      const widgetsToBeDeleted = getWidgetIdsByPage(pageToDelete, pageLayout, pageHeight);

      // filter all widgets that are not on deleted page
      let newPageLayout = pageLayout.filter(pl => widgetsToBeDeleted.indexOf(pl.i) === -1);

      // We are deleting last page, we don't need to translate any widget
      if (pageToDelete === pageBreaks - 1) {
        setPageLayout(newPageLayout);
      } else {
        newPageLayout = newPageLayout.map(pl => {
          const npl = { ...pl };
          const yPX = gridVerticalUnitsToPX(pl.y);
          const widgetPage = Math.floor(yPX / pageHeight);
          if (widgetPage > pageToDelete) {
            npl.y = pXToGridVerticalUnits(yPX - pageHeight);
          }
          return npl;
        });
        setPageLayout(newPageLayout);
      }

      // Remove widgets from pageLayoutData
      setPageLayoutData(pageLayoutData.filter(pl => widgetsToBeDeleted.indexOf(pl.i) === -1));
      setPageBreaks(pageBreaks - 1);
      setPageToDelete(null);
    }
  };

  const insertTemplate = async (
    template: ReportTemplateDetail,
    layout: Layout[],
    item?: Layout,
    mappingData?: ReportMappingData
  ) => {
    setInsertingTemplate(true);
    // Extract mapping data from the template if not provided
    const templateMappingData =
      mappingData || ((template as unknown) as { data_reference_mapping: ReportMappingData }).data_reference_mapping;
    if (template.kind === 'workspace' && templateMappingData) {
      setDataReferenceMapping(templateMappingData);
    }
    const pageHeight = getPageHeight();
    let pagesToAdd = template.page_count;
    const posPageNumber = Math.ceil(gridVerticalUnitsToPX(item?.y || 1) / pageHeight) - 1;
    let firstPageForTemplateInReport = posPageNumber + 1;
    const itemsInPage = getWidgetIdsByPage(posPageNumber, layout, pageHeight);
    if (itemsInPage.length === 0) {
      pagesToAdd -= 1;
      firstPageForTemplateInReport -= 1;
    } else {
      // detect the next page we can insert a page at
      let cond = pageContainsMultiPagesTableFromPrevPages(firstPageForTemplateInReport, pageHeight, layout);
      while (cond) {
        firstPageForTemplateInReport += 1;
        cond = pageContainsMultiPagesTableFromPrevPages(firstPageForTemplateInReport, pageHeight, layout);
      }
    }
    const allDSToMap: string[] = [];
    const allDataRefsToMap: string[] = [];

    const widgetsDataToAdd: LayoutData[] = [];
    // minY/minId to help us keep track of the first element in a template to scroll the page to
    let minY = 0,
      minId = '';
    const widgetsToAdd = (template.data as { pageLayout: Layout[] }).pageLayout.map(pl => {
      const newId = uuid();
      const pld = (template.data as { pageLayoutData: LayoutData[] }).pageLayoutData.find(opld => opld.i === pl.i);
      if (minY === 0 || pl.y < minY) {
        minY = pl.y;
        minId = newId;
      }
      if (pld) {
        if (pld[CARD_TYPES.WIDGET]) {
          if (template.kind === 'futrli') {
            const { mappedDS, originalDS, DSToMap } = mapTemplateDatasets(pld[CARD_TYPES.WIDGET]?.selectedDatasets);
            allDSToMap.push(...DSToMap);
            widgetsDataToAdd.push({
              ...pld,
              i: newId,
              [CARD_TYPES.WIDGET]: replaceRefsInWidget(pld[CARD_TYPES.WIDGET], originalDS, mappedDS, [])
            } as LayoutData);
          } else if (template.kind === 'workspace') {
            if (template.organisation_id === organisationId) {
              widgetsDataToAdd.push({
                ...pld,
                i: newId
              } as LayoutData);
            } else {
              // mapping ds
              const { mappedDS, originalDS, DSToMap } = mapWorkspaceTemplateDatasets(
                pld[CARD_TYPES.WIDGET]?.selectedDatasets,
                dataReferenceMapping || templateMappingData,
                forecasts
              );
              allDSToMap.push(...DSToMap);

              // mapping data references
              const fieldsToMap = mapWorkspaceTemplateDataRefs(
                pld[CARD_TYPES.WIDGET]?.selectedFinFields,
                allFinFieldsArray,
                rowFormulaData
              );
              allDataRefsToMap.push(...fieldsToMap);
              widgetsDataToAdd.push({
                ...pld,
                i: newId,
                [CARD_TYPES.WIDGET]: replaceRefsInWidget(pld[CARD_TYPES.WIDGET], originalDS, mappedDS, [])
              } as LayoutData);
            }
          }
        } else {
          widgetsDataToAdd.push({
            ...pld,
            i: newId
          } as LayoutData);
        }
      }
      return {
        ...pl,
        i: newId,
        y: +pl.y + pXToGridVerticalUnits(pageHeight * firstPageForTemplateInReport)
      };
    });

    onAddPageBefore(firstPageForTemplateInReport, widgetsToAdd, widgetsDataToAdd, pagesToAdd);
    heightAdjusted.current = false;

    const autoResolveAndShowMapping = async () => {
      // Auto-resolve formula refs before showing the mapping modal
      let resolvedDataRefsToMap = allDataRefsToMap.filter((v, i, arr) => arr.indexOf(v) === i);
      const templateMapping = templateMappingData;

      if (resolvedDataRefsToMap.length && template.kind === 'workspace' && templateMapping) {
        // Fetch the available line data specifically for row formulas, which may include
        // more statics (e.g. other_income, other_expenses) than the reporting line data
        let formulaAvailableFinFields: DataField[] | undefined;
        if (workspaceId && organisationId) {
          try {
            const formulaLineData = await fetchAvailableRowFormulaLineData(
              workspaceId,
              organisationId,
              isConsolidation
            );
            formulaAvailableFinFields = flattenFinFields([
              {
                ref: 'profit_and_loss',
                name: 'profit_and_loss',
                children: formulaLineData.profit_and_loss as DataField[],
                classifier: ''
              },
              {
                ref: 'balance_sheet',
                name: 'balance_sheet',
                children: formulaLineData.balance_sheet as DataField[],
                classifier: ''
              },
              {
                ref: 'cash_flow',
                name: 'cash_flow',
                children: formulaLineData.cash_flow as DataField[],
                classifier: ''
              },
              {
                ref: 'drivers',
                name: 'drivers',
                children: (formulaLineData.non_financial_drivers_and_groups || []) as DataField[],
                classifier: ''
              }
            ]);
          } catch (error) {
            Logger.warn('Could not fetch row formula available line data, falling back to reporting line data');
          }
        }

        const { autoMappedRefs, formulasToCreate, remainingRefs } = resolveFormulaRefs(
          resolvedDataRefsToMap,
          templateMapping,
          rowFormulaData,
          allFinFieldsArray,
          formulaAvailableFinFields
        );

        // Apply auto-mapped formulas (matched by expression)
        if (autoMappedRefs.length) {
          onUpdateMappedData(
            autoMappedRefs.map(([orig]) => orig),
            autoMappedRefs.map(([, mapped]) => mapped)
          );
        }

        // Auto-create formulas when all referenced line data exists in the org
        if (formulasToCreate.length && workspaceId && organisationId) {
          const createdMappings: [string, string][] = [];
          for (const { ref, mappingData: formulaMappingData } of formulasToCreate) {
            try {
              const newFormula = await createNewRowFormula(
                workspaceId,
                organisationId,
                {
                  name: formulaMappingData.name,
                  expression: formulaMappingData.data.expression as string,
                  group: (formulaMappingData.data.group || 'profit') as RowFormula['group'],
                  variance_analysis: (formulaMappingData.data.variance_analysis ||
                    'positive') as RowFormula['variance_analysis'],
                  output_type: (formulaMappingData.data.output_type || 'currency') as RowFormula['output_type']
                },
                isConsolidation
              );
              createdMappings.push([ref, newFormula.ref]);
            } catch (error) {
              // If creation fails, fall back to manual mapping
              remainingRefs.push(ref);
            }
          }

          if (createdMappings.length) {
            onUpdateMappedData(
              createdMappings.map(([orig]) => orig),
              createdMappings.map(([, mapped]) => mapped)
            );
            // Refresh row formulas to include the newly created ones
            getRowFormulas();
          }
        }

        resolvedDataRefsToMap = remainingRefs;
      }

      if (allDSToMap.length || resolvedDataRefsToMap.length) {
        setDatasetsToMap(allDSToMap.filter((v, i, arr) => arr.indexOf(v) === i));
        setDataRefsToMap(resolvedDataRefsToMap);
        if (template.kind === 'futrli') {
          setMapDatasetsModalOpen(true);
        }
        if (template.kind === 'workspace') {
          setMapTemplateDataModalOpen(true);
        }
      }
    };

    setTimeout(() => {
      setIsPendingNewFinData(true);
      setInsertingTemplate(false);
      minId && setItemToBringIntoView(minId);
      autoResolveAndShowMapping();
    }, FETCH_DATA_DELAY);
  };

  const onUpdateDatasets = (origDS: string[], mapDS: string[]) => {
    if (origDS.length && JSON.stringify(origDS) !== JSON.stringify(mapDS)) {
      const newPageLayoutData = pageLayoutData.map(pld => {
        let newPLD = pld;
        if (pld[CARD_TYPES.WIDGET]?.selectedDatasets?.length || pld[CARD_TYPES.WIDGET]?.selectedComparisons?.length) {
          newPLD = {
            ...pld,
            [CARD_TYPES.WIDGET]: replaceRefsInWidget(pld[CARD_TYPES.WIDGET], origDS, mapDS, [])
          };
        }
        return newPLD;
      });
      setPageLayoutData(newPageLayoutData);
      setIsAwaitingData(true);
      setTimeout(() => {
        setIsAwaitingData(false);
        setIsPendingNewFinData(true);
      }, FETCH_DATA_DELAY);
    }
  };

  const onUpdateMappedData = (originalRefs: string[], mappedRefs: string[]) => {
    if (originalRefs.length && JSON.stringify(originalRefs) !== JSON.stringify(mappedRefs)) {
      setPageLayoutData(prevPLD =>
        prevPLD.map(pld => {
          let cardLD = pld;
          if (
            pld[CARD_TYPES.WIDGET]?.selectedDatasets?.length ||
            pld[CARD_TYPES.WIDGET]?.selectedComparisons?.length ||
            pld[CARD_TYPES.WIDGET]?.selectedFinFields?.length
          ) {
            cardLD = {
              ...pld,
              [CARD_TYPES.WIDGET]: replaceRefsInWidget(
                pld[CARD_TYPES.WIDGET],
                originalRefs,
                mappedRefs,
                allFinFieldsArray
              )
            };
          }
          return cardLD;
        })
      );
      setIsAwaitingData(true);
      // We need to replace any mapped ref inside the expression of formulas that need mapping too
      let refsUpdated = false;
      const lineDataRefsArr = Object.keys(dataReferenceMapping?.line_data_references || {})
        .map(key => ({
          ...dataReferenceMapping?.line_data_references[key],
          ref: key
        }))
        .map(obj => {
          if (obj.data?.expression) {
            let newExp = obj.data.expression;
            originalRefs.forEach((origRef, i) => {
              newExp = newExp.replace(new RegExp(`"${origRef}"`, 'g'), `"${mappedRefs[i]}"`);
            });
            if (!refsUpdated && newExp !== obj.data.expression) {
              refsUpdated = true;
            }
            return { ...obj, data: { ...obj.data, expression: newExp } };
          } else {
            return obj;
          }
        });
      const lineDataRefs: Record<string, MappingData> = {};
      lineDataRefsArr.forEach(obj => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ref: _, ...newObj } = obj;
        lineDataRefs[obj.ref] = newObj as MappingData;
      });
      if (refsUpdated) {
        setDataReferenceMapping({ ...dataReferenceMapping, line_data_references: lineDataRefs } as ReportMappingData);
      }
      setTimeout(() => {
        setIsAwaitingData(false);
        setIsPendingNewFinData(true);
      }, FETCH_DATA_DELAY);
    }
  };

  const onDrop = async (properties: DroppedEvent) => {
    const { layout, event: e } = properties;
    const itemDataRaw = ((e as unknown) as DragEvent).dataTransfer?.getData('itemData');
    if (!itemDataRaw) {
      return;
    }
    const item = layout?.pop();
    const itemData = JSON.parse(itemDataRaw);
    if (itemData?.type === 'template') {
      setInsertingTemplate(true);
      const template = await fetchTemplateById(itemData.templateId);
      if (!template) {
        Logger.warn('Could not fetch template details!');
        return;
      }
      const mappingData: ReportMappingData = ((template as unknown) as { data_reference_mapping: ReportMappingData })
        .data_reference_mapping;
      insertTemplate(template, layout, item, mappingData);
      trackEvent(TRACKING_EVENTS.ADDED_TEMPLATE_TO_REPORT, { templateName: template?.name || '' });
      return;
    }

    // positioning new item either all the way to the left or all the way to the right
    const stepDroppedOn = e?.currentTarget
      ? getWidthStepFromPosition(e.currentTarget, e.clientX)
      : item?.x || itemData.item.x; // warning - these item.x values seem unreliable
    const x = getCorrectedTargetWidthStep(stepDroppedOn, itemData.item.w);

    const { spaceAbove, spaceBelow } = getAvailableSpace(
      {
        ...item,
        ...itemData.item,
        x,
        y: item?.y || itemData.item.y,
        h: itemData.item.h,
        w: itemData.item.w
      },
      pageLayout
    );
    // adjusting vertical position in case we don't have enough space
    let y = item?.y || itemData.item.y;
    let h = itemData.item.h;
    if (spaceBelow !== undefined && spaceBelow < h) {
      const totalSpace = (spaceBelow as number) + spaceAbove;
      y = y - spaceAbove;
      h = h > totalSpace ? totalSpace : h;
    }

    updateLayoutState([...pageLayout, { ...item, ...itemData.item, x, y, h }]);
    setItemToBringIntoView(itemData.item.i);

    if (!isOnboarding) {
      setCurrentCardId(itemData.item.i);
    }

    if (itemData.type === CARD_TYPES.WIDGET) {
      const isBarOrLineChart = [
        WIDGET_TYPES.AREA,
        WIDGET_TYPES.BAR_HORIZONTAL,
        WIDGET_TYPES.BAR_MULTI_HORIZONTAL,
        WIDGET_TYPES.BAR_MULTI_VERTICAL,
        WIDGET_TYPES.BAR_VERTICAL,
        WIDGET_TYPES.LINE,
        WIDGET_TYPES.LINE_HORIZONTAL
      ].includes(itemData.data.widgetType as WIDGET_TYPES);
      const defaultOptions = isBarOrLineChart ? { showZeroLine: true, showForecastStartLine: false } : {};
      setPageLayoutData([
        ...pageLayoutData,
        {
          i: itemData.item.i,
          type: itemData.type,
          [CARD_TYPES.WIDGET]: {
            widgetType: itemData.data.widgetType,
            widgetTitle: itemData.data.widgetTitle,
            widgetSubTitle: itemData.data.widgetSubTitle,
            selectedDatasets: [],
            selectedFinFields: [],
            selectedCalFields: [],
            selectedVariances: [],
            calendarOptions: defaultCalendarOptions,
            primaryAxisOptions: undefined,
            finFieldsOptions: [],
            tableShouldPaginate: orientation !== 'dashboard',
            ...defaultOptions
          },
          version: WIDGET_VERSION
        }
      ]);
      if (itemData.data.widgetType === 'table') {
        trackEvent(TRACKING_EVENTS.ADDED_TABLE_TO_REPORT);
      } else if (itemData.data.widgetType === 'data-card') {
        trackEvent(TRACKING_EVENTS.ADDED_DATA_CARD_TO_REPORT);
      } else {
        trackEvent(TRACKING_EVENTS.ADDED_CHART_TO_REPORT);
      }
    }
    if (itemData.type === CARD_TYPES.TEXT) {
      setPageLayoutData([
        ...pageLayoutData,
        {
          i: itemData.item.i,
          type: itemData.type,
          [CARD_TYPES.TEXT]: {
            html: ''
          }
        }
      ]);
      trackEvent(TRACKING_EVENTS.ADDED_TEXT_TO_REPORT);
    }
    if (itemData.type === CARD_TYPES.IMAGE) {
      setPageLayoutData([
        ...pageLayoutData,
        {
          i: itemData.item.i,
          type: itemData.type,
          [CARD_TYPES.IMAGE]: {
            id: itemData.data.id
          }
        }
      ]);
      trackEvent(TRACKING_EVENTS.ADDED_IMAGE_TO_REPORT);
    }
    if (itemData.type === CARD_TYPES.APP_WIDGET) {
      setPageLayoutData([
        ...pageLayoutData,
        {
          i: itemData.item.i,
          type: itemData.type,
          [CARD_TYPES.APP_WIDGET]: {
            name: itemData.data.name || APP_WIDGET_NAMES.CASH_IN_FROM_INVOICES,
            period: itemData.data.period
          }
        }
      ]);
      trackEvent(TRACKING_EVENTS.ADDED_WIDGET_TO_REPORT);
    }
    heightAdjusted.current = false;
  };

  const updateLayoutState = (layout: Layout[]) => {
    if (insertTemplateId || layout.find(e => e.i === '__dropping-elem__')) {
      return;
    }
    if (orientation === 'dashboard') {
      setPageLayout(layout);
      return;
    }
    const stepsPerPage =
      Math.floor((getPageHeight() - inchesToPX(margins[0].value) - inchesToPX(margins[2].value)) / LAYOUT_STEP) - 1;

    setPageLayout(cleanUpLayout(layout, pageLayoutData, stepsPerPage, headerFooterLimits.current));
  };

  const onBeforeGetContent = async () => {
    setCurrentCardId('');
    setPrintState(true);
  };

  const onAfterPrint = async () => {
    setPrintState(false);
  };

  const closeMenus = () => {
    if (isPageSetupMenuOpen || isZoomDropdownOpen) {
      setIsPageSetupMenuOpen?.(false);
      setIsZoomDropdownOpen?.(false);
    }
  };

  const toggleSettingsMenu = (open: boolean) => {
    setIsPageSetupMenuOpen(open);
  };
  const toggleZoomMenu = (open: boolean) => {
    setIsZoomDropdownOpen(open);
  };

  const handleCanvasClick = () => {
    setCurrentCardId('');
  };

  const updateContentScrollPosition = useCallback(() => {
    setContentScrollPosition(scrollableContent.current?.scrollTop || 0);
  }, []);

  useLayoutEffect(() => {
    if (isOnboarding) {
      updateContentScrollPosition();
      const scrollableContentElement = scrollableContent.current;
      scrollableContentElement?.addEventListener('scroll', updateContentScrollPosition);

      return () => {
        scrollableContentElement?.removeEventListener('scroll', updateContentScrollPosition);
      };
    } else {
      scrollableContent.current?.removeEventListener('scroll', updateContentScrollPosition);
    }
  }, [isOnboarding, updateContentScrollPosition]);

  return (
    <div
      className={clsx(styles.pageContainer, { [styles.onboarding]: isOnboarding })}
      data-testid="report-builder-page"
      onClick={closeMenus}
    >
      {isOnboarding ? (
        <OnboardingTopBar />
      ) : (
        <TopBar
          toggleSettingsMenu={toggleSettingsMenu}
          toggleZoomMenu={toggleZoomMenu}
          isPageSetupMenuOpen={isPageSetupMenuOpen}
          isZoomDropdownOpen={isZoomDropdownOpen}
          contentToPrint={componentRef.current}
          onBeforeGetContent={onBeforeGetContent}
          onAfterPrint={onAfterPrint}
          isDashboard={orientation === 'dashboard'}
        />
      )}
      {isPageSetupMenuOpen && <PageSetupMenu toggleMenu={toggleSettingsMenu} />}
      {isZoomDropdownOpen && orientation !== 'dashboard' && <ZoomMenu toggleMenu={toggleZoomMenu} />}
      <div className={clsx(styles.content)}>
        {orientation === 'dashboard' ? (
          <div
            className={clsx(styles.workArea, sideBarOpen && styles.workAreaWithSidebarOpen)}
            ref={workAreaRef}
            style={{ backgroundColor }}
          >
            {isOnboarding && (
              <>
                <OnboardingReportHeader scrollPosition={contentScrollPosition} />
                <OnboardingProgressDetection />
                <BottomControls onEnd={handleEnd} />
              </>
            )}
            <div
              className={clsx(styles.workAreaInner, isOnboarding && styles.onboarding)}
              ref={scrollableContent}
              tabIndex={0}
              style={{ height: workAreaHeight }}
            >
              <ReportIntlProvider locale={locale}>
                {!isOnboarding && <ReportTitle />}
                <ReportRenderer
                  isDashboard
                  isEditable
                  setItemToDelete={setItemToDelete}
                  onDuplicateItem={onDuplicateItem}
                  printState={printState}
                  updateLayoutState={updateLayoutState}
                  onDrop={onDrop}
                  setPageToDelete={setPageToDelete}
                  handleCanvasClick={handleCanvasClick}
                  containerRef={componentRef}
                />
              </ReportIntlProvider>
            </div>
            {showLoader && (
              <div className={styles.workAreaLoaderContainer}>
                <UILoader />
              </div>
            )}
          </div>
        ) : (
          <div className={clsx(styles.workArea, sideBarOpen && styles.workAreaWithSidebarOpen)} ref={workAreaRef}>
            {isOnboarding && (
              <>
                <OnboardingReportHeader scrollPosition={contentScrollPosition} />
                <OnboardingProgressDetection />
                <BottomControls onEnd={handleEnd} />
              </>
            )}
            <div
              className={clsx(styles.workAreaInner, isOnboarding && styles.onboarding)}
              ref={scrollableContent}
              tabIndex={0}
              style={{ height: workAreaHeight }}
            >
              <ReportIntlProvider locale={locale}>
                {!isOnboarding && <ReportTitle />}
                <ReportRenderer
                  isEditable
                  setItemToDelete={setItemToDelete}
                  onDuplicateItem={onDuplicateItem}
                  printState={printState}
                  updateLayoutState={updateLayoutState}
                  onDrop={onDrop}
                  setPageToDelete={setPageToDelete}
                  handleCanvasClick={handleCanvasClick}
                  containerRef={componentRef}
                  addPageBefore={onAddPageBefore}
                  duplicatePage={onDuplicatePage}
                  movePageUp={onMovePageUp}
                  movePageDown={onMovePageDown}
                />
              </ReportIntlProvider>
              {!isOnboarding && (
                <div className={styles.addPageButton} onClick={onAddPageClick}>
                  <FormattedMessage id="reporting.add_page" defaultMessage="Add page" />
                </div>
              )}
            </div>
            {showLoader && (
              <div className={styles.workAreaLoaderContainer}>
                <UILoader />
              </div>
            )}
          </div>
        )}
        <Sidebar sideBarOpenChange={v => setSidebarOpen(v)} />
      </div>
      <DeleteItemModal isOpen={itemToDelete !== null} onClose={() => setItemToDelete(null)} onDelete={onDeleteItem} />
      <DeletePageModal isOpen={pageToDelete !== null} onClose={() => setPageToDelete(null)} onDelete={onDeletePage} />
      {mapDatasetsModalOpen && (
        <MapDatasetsFutrliTemplateModal
          isOpen={mapDatasetsModalOpen}
          onClose={() => setMapDatasetsModalOpen(false)}
          onSave={onUpdateDatasets}
        />
      )}
      {mapTemplateDataModalOpen && (
        <MapTemplateDataModal
          isOpen={mapTemplateDataModalOpen}
          onClose={() => setMapTemplateDataModalOpen(false)}
          onSave={onUpdateMappedData}
        />
      )}
    </div>
  );
};
